import { findOptionByValue } from "@base/utils";
import { currencies } from "@base/utils/constants";
import { WithAnalytics } from "@base/utils/hoc/withSendPageAnalytics";
import Button from "@components/atoms/button";
import DropDown from "@components/atoms/dropDown";
import TextField from "@components/atoms/input";
import Header from "@components/molecules/header";
import Layout from "@components/molecules/layout";
import LocationSearchBox from "@components/molecules/locationSearchBox";
import { SuggestionProps } from "@components/molecules/locationSearchBox/locationSearchBox.type";
import { StripeModal } from "@components/molecules/stripe-modal";
import SwitchWrapper from "@components/molecules/switch-wrapper";
import TransactionFeeDisplay from "@components/molecules/transaction-fee";
import { LoaderSnackbarWrapperActions } from "@components/organisms/LoaderSnackbarWrapper/LoaderSnackbarWrapper.slice";
import {
  LocalizationProvider,
  MobileDateTimePicker,
} from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { DemoItem } from "@mui/x-date-pickers/internals/demo";
import dayjs, { Dayjs } from "dayjs";
import localizedFormat from "dayjs/plugin/localizedFormat";
import timezone from "dayjs/plugin/timezone";
import utc from "dayjs/plugin/utc";
import { useFormik } from "formik";
import { useEffect, useState } from "react";
import { ConnectedProps, connect } from "react-redux";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import uuid4 from "uuid4";
import * as Yup from "yup";

import { gameDetailsActions } from "../game-details/gameDetails.slice";
import { gameActions } from "../game/game.slice";
import { GameProps } from "../game/game.type";
import { transactionsActions } from "../transactions/transactions.slice";
import { gameEditActions } from "./gameEdit.slice";
import { Content, Form, PaymentOptions } from "./gameEdit.styled";
import { ComponentProps, InitialValuesProps } from "./gameEdit.type";

type ReduxProps = ConnectedProps<typeof connector>;

const GameEdit = ({
  setAlert,
  resetSelectedGame,
  editGame,
  getGameById,
  deleteGame,
  getLocation,
  setSelectedGame,
  retrieveLocation,
  getGameTransactionFee,
  userHasStripeAccount,
}: ReduxProps & ComponentProps) => {
  dayjs.extend(utc);
  const sessionToken = uuid4();
  dayjs.extend(timezone);
  dayjs.extend(localizedFormat);
  const navigate = useNavigate();
  const location = useLocation();
  const { id } = useParams();
  const [locationSuggestions, setLocationSuggestions] = useState<
    SuggestionProps[]
  >([]);
  const [initialGame, setInitialGame] = useState<GameProps>();
  const [showStripeButton, setShowStripeButton] = useState<any>(null);
  const [stripeAccount, setStripeAccount] = useState<any>(null);
  const [showFeeCalc, setShowFeeCalc] = useState<boolean>(false);

  const initialValues: InitialValuesProps = {
    title: "",
    location: "",
    address: {},
    startDate: null,
    endDate: null,
    currency: "",
    max: 0,
    fee: 0,
    chat: "",
    info: "",
    accept_cash_payment: false,
    accept_online_payment: false,
    available_on_marketplace: false,
    accept_app_payment: false,
  };

  const hasStripeConnectAccount = async () => {
    const response = await userHasStripeAccount();
    setStripeAccount(response?.payload);
  };

  const formik = useFormik({
    initialValues: initialValues,
    validationSchema: Yup.object({
      title: Yup.string().required("Required"),
      location: Yup.string().required("Required"),
      startDate: Yup.date().required("Required"),
      currency: Yup.string().required("Required"),
      endDate: Yup.date()
        .required("Required")
        .test(
          "is-greater",
          "End date must be greater than start date",
          function (value) {
            const { startDate } = this.parent;
            return startDate && value ? value > startDate : true;
          }
        ),
      fee: Yup.number()
        .typeError("Please enter a valid number")
        .required("Required"),
      max: Yup.number()
        .typeError("Please enter a valid number")
        .required("Required"),
    }),
    onSubmit: () => {},
  });

  const handleBack = () => {
    navigate("/game-details", {
      state: { from: location?.state?.from || "/home" },
    });
  };

  const handleCashChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    formik.setFieldValue("accept_cash_payment", event.target.checked);
  };

  const handleOnlineBankTransferChange = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    formik.setFieldValue("accept_online_payment", event.target.checked);
  };

  const handleIsAvailableOnMarketplace = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    formik.setFieldValue("available_on_marketplace", event.target.checked);
  };

  const handleOnlinePaymentChange = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    if (event.target.checked === true) {
      if (stripeAccount?.details_submitted == true) {
        formik.setFieldValue("accept_app_payment", event.target.checked);
        setShowFeeCalc(event.target.checked);
      } else {
        setShowStripeButton(true);
      }
    } else {
      setShowFeeCalc(event.target.checked);
      formik.setFieldValue("accept_app_payment", event.target.checked);
    }
  };

  const handleSubmit = async () => {
    if (id) {
      const body = {
        title: formik.values.title,
        description: formik.values.info,
        location: formik.values.location,
        address: formik.values.address,
        start_date: formik.values.startDate?.toISOString(),
        end_date: formik.values.endDate?.toISOString(),
        currency: formik.values.currency,
        fee: formik.values.fee,
        max_players: formik.values.max,
        chat_link: formik.values.chat,
        accept_cash_payment: formik.values.accept_cash_payment,
        accept_online_payment: formik.values.accept_online_payment,
        available_on_marketplace: formik.values.available_on_marketplace,
        accept_app_payment: formik.values.accept_app_payment,
      };
      const response = await editGame({ id, body: body });
      if (response.meta.requestStatus === "fulfilled") {
        setSelectedGame(response?.payload);
        setAlert({
          alert: {
            isVisible: true,
            message: `game updated`,
            type: "success",
          },
        });
        navigate("/game-details");
      }
    }
  };

  const onSuggestionClick = (location: any) => {
    formik.setFieldValue("location", location?.name);
    retrieveAddress(location?.mapbox_id);
  };

  const handleInitialValue = async () => {
    if (id) {
      const response = await getGameById({ id, isAuth: true });
      if (response.meta.requestStatus === "fulfilled") {
        setInitialGame(response.payload);
      }
    }
  };

  const fetchLocation = async (loc: string) => {
    const res = await getLocation({ location: loc, sessionToken });
    if (res?.meta.requestStatus === "fulfilled") {
      setLocationSuggestions(res?.payload?.suggestions);
    }
  };

  const retrieveAddress = async (id: string) => {
    const res = await retrieveLocation({ mapboxId: id, sessionToken });
    if (res?.meta.requestStatus === "fulfilled") {
      formik.setFieldValue("address", res?.payload?.features[0]);
    }
  };

  const handleDelete = async () => {
    if (id) {
      const response = await deleteGame(id);
      if (response.meta.requestStatus === "fulfilled") {
        resetSelectedGame();
        navigate("/home");
        setAlert({
          alert: {
            isVisible: true,
            message: `game has been deleted`,
            type: "success",
          },
        });
      }
    }
  };

  const handleStartDateChange = (date: Dayjs | null) => {
    formik.setFieldValue("startDate", date);
  };

  const handleEndDateChange = (date: Dayjs | null) => {
    formik.setFieldValue("endDate", date);
  };

  useEffect(() => {
    handleInitialValue();
    hasStripeConnectAccount();
  }, []);

  useEffect(() => {
    if (initialGame) {
      formik.setFieldValue("title", initialGame?.title);
      formik.setFieldValue("location", initialGame?.location);
      formik.setFieldValue("fee", initialGame?.fee);
      formik.setFieldValue("max", initialGame?.max_players);
      formik.setFieldValue("chat", initialGame?.chat_link);
      formik.setFieldValue("info", initialGame?.description);
      formik.setFieldValue("currency", initialGame?.currency);
      if (initialGame?.start_date) {
        handleStartDateChange(dayjs(initialGame?.start_date));
      }
      if (initialGame?.end_date) {
        handleEndDateChange(dayjs(initialGame?.end_date));
      }
      formik.setFieldValue(
        "accept_cash_payment",
        initialGame?.accept_cash_payment
      );
      formik.setFieldValue("address", initialGame?.address);
      formik.setFieldValue(
        "accept_online_payment",
        initialGame?.accept_online_payment
      );
      formik.setFieldValue(
        "available_on_marketplace",
        initialGame?.available_on_marketplace
      );
      formik.setFieldValue(
        "accept_app_payment",
        initialGame?.accept_app_payment
      );
      setShowFeeCalc(initialGame?.accept_app_payment || false);
    }
  }, [initialGame]);

  return (
    <Layout
      isLayout
      HeaderChildren={
        <Header title="Edit game details" handleBack={handleBack} />
      }
    >
      <Content>
        <Form>
          <TextField
            name="title"
            placeholder="Title"
            label="Title"
            fullWidth
            onBlur={formik.handleBlur}
            onChange={formik.handleChange}
            value={formik.values.title}
            touched={formik.touched.title}
            errorMessage={formik.errors.title}
          />
          {
            //@ts-ignore
            <LocationSearchBox
              placeholder={"Location"}
              name="location"
              fullWidth
              label="Location"
              value={formik.values.location}
              onChange={(e) => {
                formik.setFieldValue("location", e.target.value);
                if (e.target.value.length > 2) fetchLocation(e.target.value);
              }}
              onSuggestionClick={onSuggestionClick}
              data={locationSuggestions}
              onBlur={formik.handleBlur}
              errorMessage={formik.errors.location}
              touched={formik.touched.location}
            />
          }

          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <DemoItem>
              <MobileDateTimePicker
                sx={{
                  "& .MuiInputBase-input": {
                    height: "auto",
                  },
                }}
                label="Start Date"
                onChange={handleStartDateChange}
                value={formik.values.startDate}
                disablePast
              />
            </DemoItem>
          </LocalizationProvider>
          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <DemoItem>
              <MobileDateTimePicker
                sx={{
                  "& .MuiInputBase-input": {
                    height: "auto",
                  },
                }}
                label={"End Date"}
                onChange={handleEndDateChange}
                value={formik.values.endDate}
                disablePast
                slotProps={{
                  textField: {
                    error: Boolean(formik.errors.endDate),
                    helperText: formik.errors.endDate,
                  },
                }}
              />
            </DemoItem>
          </LocalizationProvider>
          <TextField
            name="fee"
            placeholder="Fee"
            label="Fee"
            fullWidth
            onBlur={formik.handleBlur}
            onChange={formik.handleChange}
            value={formik.values.fee?.toString() || ""}
            touched={formik.touched.fee}
            errorMessage={formik.errors.fee}
          />
          <DropDown
            name="currency"
            placeholder="Currency"
            options={currencies}
            defaultValue={findOptionByValue(currencies, formik.values.currency)}
            onChange={(e, option) =>
              formik.setFieldValue("currency", option?.value)
            }
            onBlur={formik.handleBlur}
            errorMessage={formik.errors.currency}
            touched={formik.touched.currency}
          />
          <PaymentOptions>
            <SwitchWrapper
              label="Accept cash"
              checked={formik.values.accept_cash_payment}
              handleChange={handleCashChange}
            />
            <SwitchWrapper
              label="Accept bank transfer"
              checked={formik.values.accept_online_payment}
              handleChange={handleOnlineBankTransferChange}
            />
            <SwitchWrapper
              label="Accept in-app payment"
              checked={formik.values.accept_app_payment}
              handleChange={handleOnlinePaymentChange}
            />
            <TransactionFeeDisplay
              getGameTransactionFee={getGameTransactionFee}
              currency={formik.values.currency}
              fee={formik.values.fee}
              game_id={id}
              isOpen={showFeeCalc}
            />
          </PaymentOptions>
          <TextField
            name="max"
            placeholder="Max numbers of players"
            label="Max numbers of players"
            fullWidth
            onBlur={formik.handleBlur}
            onChange={formik.handleChange}
            value={formik.values.max?.toString()}
            touched={formik.touched.max}
            errorMessage={formik.errors.max}
          />
          <TextField
            name="chat"
            placeholder="Game Chat"
            label="Game Chat"
            fullWidth
            onBlur={formik.handleBlur}
            onChange={formik.handleChange}
            value={formik.values.chat}
            touched={formik.touched.chat}
            errorMessage={formik.errors.chat}
          />
          <TextField
            name="info"
            placeholder="Additional Information"
            label="Additional Information"
            multiline
            rows={4}
            fullWidth
            onBlur={formik.handleBlur}
            onChange={formik.handleChange}
            value={formik.values.info}
            touched={formik.touched.info}
            errorMessage={formik.errors.info}
          />
          <PaymentOptions>
            <SwitchWrapper
              label="Available on marketplace"
              checked={formik.values.available_on_marketplace}
              handleChange={handleIsAvailableOnMarketplace}
            />
          </PaymentOptions>
          <Button
            isDisabled={!formik.isValid}
            onClick={handleSubmit}
            text="Save Changes"
            color="secondary"
            variant="contained"
            type="button"
            textColor={"#FFF"}
          />
          <Button
            color="error"
            onClick={handleDelete}
            text="Delete Game"
            variant="contained"
            type="button"
            textColor={"#FFF"}
          />
          <StripeModal
            modalTitle="Payments Account Required"
            modalButtonText="Create Payments Account"
            handleClose={() => {
              setShowStripeButton(false);
            }}
            isOpen={showStripeButton}
          />
        </Form>
      </Content>
    </Layout>
  );
};
const mapDispatchToProps = {
  setSelectedGame: gameDetailsActions.setSelectedGame,
  editGame: gameEditActions.editGame,
  getGameById: gameDetailsActions.getGameById,
  deleteGame: gameEditActions.deleteGame,
  getLocation: gameActions.getLocation,
  setAlert: LoaderSnackbarWrapperActions.setGlobalAppearances,
  resetSelectedGame: gameDetailsActions.reset,
  retrieveLocation: gameActions.retrieveLocation,
  getGameTransactionFee: gameActions.getGameTransactionFee,
  userHasStripeAccount: transactionsActions.userHasStripeAccount,
};

const connector = connect(undefined, mapDispatchToProps);
const GameEditRedux = connector(WithAnalytics(GameEdit));

export { GameEditRedux as GameEdit };
